import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnInfoToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {usePlantUpmId} from './usePlantUpmId'

export enum AlertName {
  DAILY_THRESHOLDS = 'ThresholdViolationsDailySummary',
  DAILY_ANOMALIES = 'ContextualAnomaliesDailySummary',
  ONEVENT_THRESHOLDS = 'ThresholdViolationsOnEvent',
  ONEVENT_ANOMALIES = 'ContextualAnomaliesOnEvent'
}

type AlertSubscription = {
  id: string
  alertId: string
  alertName: AlertName
  userId: string
  hierarchyId: string
}

export const QueryKey = 'alerts'

export const useGetAlertSubscription = () => {
  const hierarchyId = usePlantUpmId()

  return useQuery({
    queryKey: [QueryKey, hierarchyId],
    queryFn: async () => {
      const path = '/custom-alerts/subscriptions/me'
      return (await ApiClient.axiosInstance.get<AlertSubscription[]>(path, {params: {hierarchyId}}))
        .data
    },
    retry: false,
    enabled: !!hierarchyId,
    staleTime: Infinity
  })
}

export const useCreateAlertSubscription = () => {
  const hierarchyId = usePlantUpmId()

  return useMutation({
    mutationFn: async (alertName: AlertName) => {
      const path = '/custom-alerts/subscriptions'
      return (
        await ApiClient.axiosInstance.post<void>(path, {alertIdOrName: alertName, hierarchyId})
      ).data
    },
    onSuccess: () => {
      spawnSuccessToast("You'll now receive email notifications", {
        toastId: 'alert-subscription-success',
        closeOnClick: true
      })
    },
    onError: (error: unknown) => {
      spawnErrorToast(
        `Error creating alert subscription: ${
          error instanceof AxiosError ? error.message : 'Unknown error'
        }`,
        {toastId: 'alert-subscription-error', closeOnClick: true}
      )
    }
  })
}

export const useCancelAlertSubscription = () => {
  return useMutation({
    mutationFn: async (alertId: string) => {
      const path = `/custom-alerts/subscriptions/${alertId}/cancellation`
      return (await ApiClient.axiosInstance.put<void>(path)).data
    },
    onSuccess: () => {
      spawnInfoToast('Notifications disabled', {
        toastId: 'alert-subscription-cancelled',
        closeOnClick: true
      })
    },
    onError: (error: unknown) => {
      spawnErrorToast(
        `Error cancelling alert subscription: ${
          error instanceof AxiosError ? error.message : 'Unknown error'
        }`,
        {
          toastId: 'alert-subscription-cancel-error',
          closeOnClick: true
        }
      )
    }
  })
}
